import { ICallTrackingLogCalls, ICallTrackingNumbers } from '@llws/lift-entity-interfaces';
import { ActionContext } from 'vuex';
import dayjs from 'dayjs';
import {
  Between,
  Equal,
  IQueryCommandOptions,
  IWhereCommand,
  In,
  convertQueryCommandToQueryString,
} from '@llws/dynamic-router-utils';
import { IExportFilterPayloadV2, IFilterPayloadV2 } from '@/shared';
import { adminRequest } from '@/shared/request-configs/admin-request';
import { ICallTrackingLogStats } from '@/shared/interfaces/call-tracking-log-stats';
import { ICallTrackingLogCallsState } from '@/shared/interfaces/call-tracking-log-calls-store';
import { IAction, IStateStrict } from '@/shared/interfaces/store-base';
import { downloadCSV } from '@/shared/helpers/download-csv';
import { TRootState } from '..';
import { StoreBaseImplementationV2 } from '@/shared/classes/store-base-implementation-v2';

// Base store configuration
const adminGatewayResourcePath: string = '/call-tracking-api/call_tracking_log_calls';
const listViewName: string = '';
const showLogging: boolean = false;
const defaultGetListFilters: IQueryCommandOptions = {
  relations: [
    { path: 'partner', alias: 'partner' },
    { path: 'building', alias: 'building' },
    { path: 'building.city', alias: 'city' },
    { path: 'city.province', alias: 'province' },
    { path: 'interactiveVoiceResponse', alias: 'interactiveVoiceResponse' },
  ],
};
const defaultGetSingleFilters: IQueryCommandOptions = {};

// Instantiate the base store with the params
const base = new StoreBaseImplementationV2<ICallTrackingNumbers>({
  adminGatewayResourcePath,
  defaultGetSingleFilters,
  defaultGetListFilters,
  listViewName,
  showLogging,
});

// Extend StoreBaseImplementation object to add custom state/getters/mutations/actions.
export default {
  namespaced: base.namespaced, // 'call-tracking-log-calls/'

  state: {
    ...base.state,
    logStats: {
      countCalls: 0,
      countMessages: 0,
      avgDuration: 0,
    } as ICallTrackingLogCallsState,
  },

  getters: {
    ...base.getters,
    logStats(state: ICallTrackingLogCallsState) {
      return state.logStats;
    },
  },

  mutations: {
    ...base.mutations,
    setLogStats: (state: ICallTrackingLogCallsState, payload: ICallTrackingLogStats) => {
      state.logStats = payload;
    },
  },

  actions: {
    ...base.actions,

    async getCallLogStats(ctx: ActionContext<ICallTrackingLogCallsState, TRootState>, payload: IFilterPayloadV2) {
      const statFilters = payload?.filters || {};
      statFilters.relations = defaultGetListFilters.relations;

      let timeRange: IWhereCommand[] = [];
      let partnerKeys: string[] = [];
      let statusFilterValues: string[] = [];

      if (statFilters.where) {
        timeRange = statFilters.where.filter(
          (w) => (w as IWhereCommand).field === 'timestamp' || (w as IWhereCommand).field === 'created',
        ) as IWhereCommand[];
        partnerKeys = (statFilters.where.filter(
          (w) => (w as IWhereCommand).field === 'p.partnerKey',
        ) as IWhereCommand[])[0]?.value.split('|') || [];
        statusFilterValues = (statFilters.where.filter(
          (w) => (w as IWhereCommand).field === 'status',
        ) as IWhereCommand[])[0]?.value.split('|') || [];
      }

      const fromDate = (
        timeRange.length !== 0
          ? timeRange.find((w) => w.operator === 'gte')?.value
          : dayjs().startOf('year').format('YYYY-MM-DD')
      );

      const toDate = (
        timeRange.length !== 0 && (timeRange.find((w) => w.operator === 'lte'))
          ? timeRange.find((w) => w.operator === 'lte')?.value
          : dayjs().add(1, 'day').format('YYYY-MM-DD')
      );

      const aggregateRelations = [
        { path: 'partner', alias: 'p' },
      ];

      statFilters.alias = 'cl';
      statFilters.select = ['countCalls', 'countMessages', 'avgDuration'];

      statFilters.aggregations = [
        {
          table: 'call_tracking_log_calls',
          propertyAlias: 'countCalls',
          whereCommands: [
            Equal('clientId', ctx.rootGetters['users/currentClient'].id),
            Between('timestamp', fromDate, toDate),
          ],
          relations: aggregateRelations,
          column: 'id',
          aggregate: 'COUNT_DISTINCT',
        },
        {
          table: 'call_tracking_log_messages',
          propertyAlias: 'countMessages',
          whereCommands: [
            Equal('clientId', ctx.rootGetters['users/currentClient'].id),
            Between('timestamp', fromDate, toDate),
          ],
          relations: aggregateRelations,
          column: 'id',
          aggregate: 'COUNT_DISTINCT',
        },
        {
          table: 'call_tracking_log_calls',
          propertyAlias: 'avgDuration',
          whereCommands: [
            Equal('clientId', ctx.rootGetters['users/currentClient'].id),
            Between('timestamp', fromDate, toDate),
          ],
          relations: aggregateRelations,
          column: 'dialDuration',
          aggregate: 'AVG',
        },
      ];

      if (partnerKeys.length) {
        const updatePartnerKeysAggregation = statFilters.aggregations.map((ac) => {
          ac.whereCommands?.push(In('p.partnerKey', partnerKeys));
          return ac;
        });

        statFilters.aggregations = updatePartnerKeysAggregation;
      }

      if (statusFilterValues.length) {
        const updateStatusAggregation = statFilters.aggregations.map((ac) => {
          ac.whereCommands?.push(In('status', statusFilterValues));
          return ac;
        });

        statFilters.aggregations = updateStatusAggregation;
      }

      // Ensure only one result is returned.
      statFilters.limit = 1;

      const response = await adminRequest.get(`${adminGatewayResourcePath}${convertQueryCommandToQueryString(statFilters)}`);

      ctx.commit('setLogStats', {
        countCalls: response.data[0] ? response.data[0].countCalls : 0,
        countMessages: response.data[0] ? response.data[0].countMessages : 0,
        avgDuration: response.data[0] ? response.data[0].avgDuration : 0,
      });
    },

    async getCallTrackingLogsDataCSV(ctx: IAction<IStateStrict<ICallTrackingLogCalls>>, payload: IExportFilterPayloadV2) {
      const url = adminGatewayResourcePath + convertQueryCommandToQueryString(payload.filters);
      const response = await adminRequest.post(`${url}&csvExport=true`, { fields: payload.fields });
      downloadCSV(typeof response === 'string' ? response : '', payload.filename || `${dayjs().format('DD-MM-YYYY')}_report.csv`);
    },
  },
};
